import React, { useState, useEffect } from 'react';

interface MasteryTrackingPreviewProps {
  onClick?: () => void;
}

interface MasteryNode {
  id: string;
  label: string;
  mastery: number;
  targetMastery: number;
  isActive: boolean;
}

const MasteryTrackingPreview: React.FC<MasteryTrackingPreviewProps> = ({ onClick }) => {
  const [nodes, setNodes] = useState<MasteryNode[]>([
    { id: 'n1', label: 'Fractions', mastery: 45, targetMastery: 45, isActive: false },
    { id: 'n2', label: 'Decimals', mastery: 72, targetMastery: 72, isActive: false },
    { id: 'n3', label: 'Percentages', mastery: 38, targetMastery: 38, isActive: false },
    { id: 'n4', label: 'Ratios', mastery: 85, targetMastery: 85, isActive: false },
  ]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isAnswering, setIsAnswering] = useState(false);
  const [lastResult, setLastResult] = useState<'correct' | 'incorrect' | null>(null);
  const [xpGained, setXpGained] = useState(0);

  // Simulate mastery updates
  useEffect(() => {
    const simulateQuestion = () => {
      // Pick a random node
      const nodeIdx = Math.floor(Math.random() * nodes.length);
      const isCorrect = Math.random() > 0.35;

      // Start answering
      setIsAnswering(true);
      setNodes(prev => prev.map((n, i) => ({ ...n, isActive: i === nodeIdx })));

      // Show result after delay
      setTimeout(() => {
        setIsAnswering(false);
        setLastResult(isCorrect ? 'correct' : 'incorrect');
        setCurrentQuestion(prev => prev + 1);

        if (isCorrect) {
          setXpGained(prev => prev + 25);
        }

        // Update mastery
        setNodes(prev => prev.map((n, i) => {
          if (i === nodeIdx) {
            const change = isCorrect ? Math.min(8, 100 - n.mastery) : -Math.min(3, n.mastery);
            return {
              ...n,
              targetMastery: Math.max(0, Math.min(100, n.mastery + change)),
            };
          }
          return n;
        }));

        // Animate mastery change
        const animateMastery = () => {
          setNodes(prev => prev.map(n => {
            if (n.mastery !== n.targetMastery) {
              const diff = n.targetMastery - n.mastery;
              const step = diff > 0 ? Math.min(1, diff) : Math.max(-1, diff);
              return { ...n, mastery: n.mastery + step };
            }
            return n;
          }));
        };

        const animInterval = setInterval(animateMastery, 50);
        setTimeout(() => {
          clearInterval(animInterval);
          setNodes(prev => prev.map(n => ({ ...n, mastery: n.targetMastery, isActive: false })));
          setLastResult(null);
        }, 800);
      }, 1200);
    };

    const interval = setInterval(simulateQuestion, 3000);
    simulateQuestion();

    return () => clearInterval(interval);
  }, []);

  const getMasteryColor = (mastery: number) => {
    if (mastery >= 80) return '#10b981';
    if (mastery >= 60) return '#3b82f6';
    if (mastery >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const getMasteryLabel = (mastery: number) => {
    if (mastery >= 80) return 'Mastered';
    if (mastery >= 60) return 'Proficient';
    if (mastery >= 40) return 'Learning';
    return 'Needs Work';
  };

  const avgMastery = Math.round(nodes.reduce((sum, n) => sum + n.mastery, 0) / nodes.length);

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '320px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b flex items-center justify-between"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: lastResult === 'correct'
            ? 'rgba(16, 185, 129, 0.1)'
            : lastResult === 'incorrect'
              ? 'rgba(239, 68, 68, 0.1)'
              : 'rgba(139, 92, 246, 0.05)',
          transition: 'background 0.3s ease',
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          Real-Time Mastery
        </span>
        <div className="flex items-center gap-2">
          {lastResult === 'correct' && (
            <span className="text-[10px] font-bold animate-pulse" style={{ color: '#10b981' }}>
              +25 XP
            </span>
          )}
          <div className="flex items-center gap-1">
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: isAnswering ? '#f59e0b' : '#10b981' }}
            />
            <span className="text-[10px]" style={{ color: isAnswering ? '#f59e0b' : '#10b981' }}>
              {isAnswering ? 'UPDATING...' : 'LIVE'}
            </span>
          </div>
        </div>
      </div>

      {/* Overall Progress Ring */}
      <div className="p-4 flex items-center gap-4">
        <div className="relative">
          <svg width="70" height="70" viewBox="0 0 70 70">
            {/* Background circle */}
            <circle
              cx="35"
              cy="35"
              r="30"
              fill="none"
              stroke="rgba(255, 255, 255, 0.1)"
              strokeWidth="6"
            />
            {/* Progress circle */}
            <circle
              cx="35"
              cy="35"
              r="30"
              fill="none"
              stroke={getMasteryColor(avgMastery)}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${(avgMastery / 100) * 188.5} 188.5`}
              transform="rotate(-90 35 35)"
              style={{ transition: 'stroke-dasharray 0.3s ease' }}
            />
            {/* Center text */}
            <text
              x="35"
              y="32"
              textAnchor="middle"
              fill="white"
              fontSize="16"
              fontWeight="bold"
            >
              {avgMastery}%
            </text>
            <text
              x="35"
              y="45"
              textAnchor="middle"
              fill="#64748b"
              fontSize="8"
            >
              Overall
            </text>
          </svg>
        </div>

        <div className="flex-1">
          <div className="text-xs font-medium mb-1" style={{ color: '#e2e8f0' }}>
            Question #{currentQuestion}
          </div>
          <div className="text-[10px] mb-2" style={{ color: '#64748b' }}>
            {isAnswering ? 'Processing answer...' : 'Mastery updating in real-time'}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg">⭐</span>
            <span className="text-sm font-bold" style={{ color: '#f59e0b' }}>
              {xpGained} XP
            </span>
          </div>
        </div>
      </div>

      {/* Individual Mastery Bars */}
      <div className="px-4 pb-3 space-y-2">
        {nodes.map(node => (
          <div
            key={node.id}
            className="p-2 rounded-lg transition-all duration-300"
            style={{
              background: node.isActive ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255, 255, 255, 0.02)',
              border: node.isActive ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid transparent',
            }}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium" style={{ color: node.isActive ? '#e2e8f0' : '#94a3b8' }}>
                {node.label}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[10px]" style={{ color: getMasteryColor(node.mastery) }}>
                  {getMasteryLabel(node.mastery)}
                </span>
                <span className="text-xs font-bold" style={{ color: getMasteryColor(node.mastery) }}>
                  {Math.round(node.mastery)}%
                </span>
              </div>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${node.mastery}%`,
                  background: getMasteryColor(node.mastery),
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Spaced Repetition Info */}
      <div className="px-4 pb-3">
        <div
          className="p-2 rounded-lg flex items-center gap-2"
          style={{ background: 'rgba(6, 182, 212, 0.1)', border: '1px solid rgba(6, 182, 212, 0.2)' }}
        >
          <span className="text-sm">🔄</span>
          <div>
            <div className="text-[10px] font-medium" style={{ color: '#06b6d4' }}>
              Spaced Repetition Active
            </div>
            <div className="text-[9px]" style={{ color: '#64748b' }}>
              Fractions review scheduled for tomorrow
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <span className="text-[10px]" style={{ color: '#64748b' }}>
          Every answer updates mastery
        </span>
        <div className="flex items-center gap-1">
          <span className="text-[10px]" style={{ color: '#10b981' }}>
            Long-term retention
          </span>
        </div>
      </div>

      {/* Expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(59, 130, 246, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(139, 92, 246, 0.4)',
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default MasteryTrackingPreview;
